import { BasketDrawer } from '@common/components/BasketDrawer/BasketDrawer';
import { ModuleType } from '@common/components/BasketDrawer/BasketListing.types';
import { useModulesToRecommend } from '@common/components/BasketDrawer/hooks/useModulesToRecommend';
import {
  CloseModalButton,
  FormWrapper,
  Header,
  InnerScroller,
  MobileHeading,
  Modal,
  PrevStepButton,
} from '@common/components/BookingForms/Modal';
import { FormattedLink } from '@common/components/FormattedLink';
import { usePageContext } from '@common/components/PageContext';
import SpaEntityStickyBar from '@common/components/SpaEntity/SpaEntityStickyBar';
import { Spinner } from '@common/components/Spinner';
import { SignInModalForm } from '@features/SignIn';
import { useTranslations } from '@lang';
import {
  getDiscountForMinimumGuests,
  getPriceForMinimumGuests,
} from '@utils/getPriceForMinimumGuests';
import { FC } from 'react';
import { SubHeader } from './SpaEntityConfigWizard.styles';
import { ConfigWizardV2Props } from './SpaEntityConfigWizardV2.types';

const STICKY_BAR_ID = 'spa-entity-sticky-bar';

export const SpaEntityConfigWizardV2: FC<ConfigWizardV2Props> = ({
  isConfirmationVisible,
  basketDetails,
  hideConfirmation,
  isSignInModalOpen,
  handleSignInModalClose,
  handleSignIn,
  loginError,
  isModalOpen,
  isSpinnerVisible,
  goToPreviousStep,
  gotoEntityDetailPage,
  spaEntity,
  SpaEntityFormSteps,
  bookingStep,
  language,
  existingGuests,
  maxDaysAdvance,
  isExtrasAvailable,
  goToNextStep,
  finishWizardAction,
  selectedTimeSlot,
  goToStep,
  SpaEntityFormStepEnum,
  availableTimeslots,
  fetchingDates,
  isSpaEntityFetching,
  setStartDate,
  modulesExcludingDisabled,
  hideArrivalTimes,
  entityBookingRoute,
  entityType,
  entityLabel,
}) => {
  const { routingData } = usePageContext();
  const { t } = useTranslations();

  const modules = Object.keys(modulesExcludingDisabled);

  const modulesToRecommend = useModulesToRecommend({
    modules: modules as ModuleType[],
    items: basketDetails.items,
  });

  return (
    <>
      {isConfirmationVisible && (
        <BasketDrawer
          currencyCode={basketDetails.total.currencyCode}
          numberOfItems={basketDetails.items.length}
          open={isConfirmationVisible}
          onClose={hideConfirmation}
          title={t('basketDrawer.title')}
          totalCost={basketDetails.total.amount}
          items={basketDetails?.items}
          modules={modulesToRecommend}
          hideArrivalTimes={hideArrivalTimes}
        />
      )}
      <SignInModalForm
        isModalOpen={isSignInModalOpen}
        handleModalClose={handleSignInModalClose}
        onSubmit={handleSignIn}
        loginError={loginError}
      />
      <Modal isVisible={isModalOpen}>
        <>
          <Spinner isActive={isSpinnerVisible} showDelayMessage />
          <Header>
            <PrevStepButton onClick={goToPreviousStep} />
            <MobileHeading>{spaEntity?.name}</MobileHeading>
            <CloseModalButton onClick={gotoEntityDetailPage} />
          </Header>
          <InnerScroller scrollToTopIndicator={bookingStep}>
            <FormWrapper>
              <SubHeader>
                {routingData && (
                  <FormattedLink format="button" href={routingData.href}>
                    {routingData.message}
                  </FormattedLink>
                )}
              </SubHeader>
              <SpaEntityFormSteps
                spaEntity={spaEntity}
                language={language}
                existingGuests={existingGuests}
                maxDaysAdvance={maxDaysAdvance}
                bookingStep={bookingStep}
                isExtrasAvailable={isExtrasAvailable}
                goToNextStep={goToNextStep}
                finishWizardAction={finishWizardAction}
                availableTimeslots={availableTimeslots}
                fetchingDates={fetchingDates}
                isSpaEntityFetching={isSpaEntityFetching}
                setStartDate={setStartDate}
                entityBookingRoute={entityBookingRoute}
                entityType={entityType}
                entityLabel={entityLabel}
                SpaEntityFormStepEnum={SpaEntityFormStepEnum}
              />
            </FormWrapper>
          </InnerScroller>
        </>
      </Modal>
      {spaEntity && (
        <SpaEntityStickyBar
          id={STICKY_BAR_ID}
          name={spaEntity.name}
          displayPriceRangeIndicator
          displayedPrice={getPriceForMinimumGuests(spaEntity.price, spaEntity.minimumGuests)}
          discountedPrice={getDiscountForMinimumGuests(
            selectedTimeSlot?.discount || spaEntity.discount,
            spaEntity.minimumGuests
          )}
          isModalOpen={isModalOpen}
          onSubmit={() => goToStep(SpaEntityFormStepEnum.AVAILABILITY)}
        />
      )}
    </>
  );
};
